import type { FC } from 'react';
import { Wrench } from 'lucide-react';
import { CollapsibleFormSection } from './CollapsibleFormSection';
import { SimpleInput } from '@/components/form/inputs/SimpleInput';
import { useTranslation } from '@/hooks/useTranslation';
import { Badge } from '@/components/ui/badge';

interface ProcessFilteredFormSectionsBProps {
  visibleKeys: Set<string>;
  mandatoryFields: string[];
  formValues: Record<string, unknown>;
  onChange: (name: string, value: unknown) => void;
}

function RequiredBadge() {
  const { t } = useTranslation();
  return (
    <Badge tone="primary" size="sm" className="ml-1">
      {t('common.required')}
    </Badge>
  );
}

function FieldWrapper({
  mandatory,
  children,
}: {
  mandatory: boolean;
  children: React.ReactNode;
}) {
  if (!mandatory) return <>{children}</>;
  return (
    <div className="relative">
      <div className="absolute right-0 top-0 z-10">
        <RequiredBadge />
      </div>
      {children}
    </div>
  );
}

export const ProcessFilteredFormSectionsB: FC<ProcessFilteredFormSectionsBProps> = ({
  visibleKeys,
  mandatoryFields,
  formValues,
  onChange,
}) => {
  const { t } = useTranslation();

  const isVisible = (key: string) => visibleKeys.has(key) || mandatoryFields.includes(key);
  const isMandatory = (key: string) => mandatoryFields.includes(key);

  const toolingFields = ['lubType', 'lubBrand', 'holderType', 'holderBrand', 'clampingType', 'clampingBrand'];

  const toolingVisible = toolingFields.filter(isVisible);

  const countLabel = (visible: number, total: number) =>
    t('traceability.processManager.form.sections.countLabel', { set: visible, total });

  return (
    <>
      {toolingVisible.length > 0 && (
        <CollapsibleFormSection
          icon={<Wrench className="h-3 w-3" aria-hidden />}
          title={t('traceability.processSelection.sections.tooling')}
          toggleLabel={t('traceability.processSelection.sections.tooling')}
          countLabel={countLabel(toolingVisible.length, toolingFields.length)}
          defaultOpen
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {isVisible('lubType') && (
              <FieldWrapper mandatory={isMandatory('lubType')}>
                <SimpleInput
                  name="lubType"
                  label={t('traceability.processManager.form.prefillFields.lubType.label')}
                  value={formValues.lubType as string}
                  onChange={(v) => onChange('lubType', v)}
                  placeholder={t('traceability.processManager.form.prefillFields.lubType.placeholder')}
                  required={isMandatory('lubType')}
                />
              </FieldWrapper>
            )}
            {isVisible('lubBrand') && (
              <FieldWrapper mandatory={isMandatory('lubBrand')}>
                <SimpleInput
                  name="lubBrand"
                  label={t('traceability.processManager.form.prefillFields.lubBrand.label')}
                  value={formValues.lubBrand as string}
                  onChange={(v) => onChange('lubBrand', v)}
                  placeholder={t('traceability.processManager.form.prefillFields.lubBrand.placeholder')}
                  required={isMandatory('lubBrand')}
                />
              </FieldWrapper>
            )}
            {isVisible('holderType') && (
              <FieldWrapper mandatory={isMandatory('holderType')}>
                <SimpleInput
                  name="holderType"
                  label={t('traceability.processManager.form.prefillFields.holderType.label')}
                  value={formValues.holderType as string}
                  onChange={(v) => onChange('holderType', v)}
                  placeholder={t('traceability.processManager.form.prefillFields.holderType.placeholder')}
                  required={isMandatory('holderType')}
                />
              </FieldWrapper>
            )}
            {isVisible('holderBrand') && (
              <FieldWrapper mandatory={isMandatory('holderBrand')}>
                <SimpleInput
                  name="holderBrand"
                  label={t('traceability.processManager.form.prefillFields.holderBrand.label')}
                  value={formValues.holderBrand as string}
                  onChange={(v) => onChange('holderBrand', v)}
                  placeholder={t('traceability.processManager.form.prefillFields.holderBrand.placeholder')}
                  required={isMandatory('holderBrand')}
                />
              </FieldWrapper>
            )}
            {isVisible('clampingType') && (
              <FieldWrapper mandatory={isMandatory('clampingType')}>
                <SimpleInput
                  name="clampingType"
                  label={t('traceability.processManager.form.prefillFields.clampingType.label')}
                  value={formValues.clampingType as string}
                  onChange={(v) => onChange('clampingType', v)}
                  placeholder={t('traceability.processManager.form.prefillFields.clampingType.placeholder')}
                  required={isMandatory('clampingType')}
                />
              </FieldWrapper>
            )}
            {isVisible('clampingBrand') && (
              <FieldWrapper mandatory={isMandatory('clampingBrand')}>
                <SimpleInput
                  name="clampingBrand"
                  label={t('traceability.processManager.form.prefillFields.clampingBrand.label')}
                  value={formValues.clampingBrand as string}
                  onChange={(v) => onChange('clampingBrand', v)}
                  placeholder={t('traceability.processManager.form.prefillFields.clampingBrand.placeholder')}
                  required={isMandatory('clampingBrand')}
                />
              </FieldWrapper>
            )}
          </div>
        </CollapsibleFormSection>
      )}
    </>
  );
};
